'use client';

import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useToast} from '@/matic/components/ui/toast';
import {getMasterDataApi} from '@/matic/features/master-data/api/master-data-api';
import {masterDataKeys} from '@/matic/features/master-data/query-keys';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {isApiError} from '@/matic/lib/api/error-types';
import {trackProductEvent} from '@/matic/lib/product-observability';
import type {
  CreateObjectRecordRequest,
  UpdateObjectRecordRequest,
  ObjectRecord,
  PaginatedResponse,
} from '@/matic/features/master-data/types/master-data-types';

export type FieldErrors = Record<string, string>;

export function extractFieldErrors(error: unknown): FieldErrors | undefined {
  if (!isApiError(error) || error.status !== 400) return undefined;
  const {data} = error as {data?: unknown};
  if (data && typeof data === 'object') {
    const {params} = data as {params?: unknown};
    if (params && typeof params === 'object') {
      const {errors} = params as {errors?: unknown};
      if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
        return errors as FieldErrors;
      }
    }
  }

  return undefined;
}

export function useObjectRecordMutations(typeId: string) {
  const queryClient = useQueryClient();
  const tenantId = useRequiredTenantId();
  const {toast} = useToast();
  const api = getMasterDataApi();

  const createMutation = useMutation({
    async mutationFn(input: CreateObjectRecordRequest) {
      return api.createObjectRecord(typeId, input);
    },
    onSuccess(record) {
      toast({
        title: 'Tag created',
        description: `${record.code} has been created`,
        variant: 'success',
      });
      trackProductEvent('resource.created', {
        resourceType: 'object_record',
        typeId,
        recordId: record.id,
        code: record.code,
      });
    },
    onError(error: Error) {
      const fieldErrors = extractFieldErrors(error);
      if (fieldErrors) {
        toast({
          title: 'Validation failed',
          description: 'Please fix the highlighted fields',
          variant: 'error',
        });
        return;
      }

      toast({
        title: 'Failed to create tag',
        description: error.message,
        variant: 'error',
      });
    },
    onSettled() {
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectRecordLists(typeId),
      });
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectTypeLists(),
      });
      // The Library renders its record buckets from the batch-slice query, a
      // distinct key from objectRecordLists. Bust it so a create made from the
      // Library sidebar/browse surfaces appears immediately.
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectRecordsBatchAll(),
      });
    },
  });

  const updateMutation = useMutation({
    async mutationFn({
      recordId,
      input,
    }: {
      recordId: string;
      input: UpdateObjectRecordRequest;
    }) {
      return api.updateObjectRecord(typeId, recordId, input);
    },
    onSuccess(record, {input}) {
      toast({
        title: 'Tag updated',
        description: `${record.code} has been updated`,
        variant: 'success',
      });
      trackProductEvent('resource.updated', {
        resourceType: 'object_record',
        typeId,
        recordId: record.id,
        fieldsChanged: Object.keys(input),
      });
    },
    onError(error: Error) {
      const fieldErrors = extractFieldErrors(error);
      if (fieldErrors) {
        toast({
          title: 'Validation failed',
          description: 'Please fix the highlighted fields',
          variant: 'error',
        });
        return;
      }

      toast({
        title: 'Failed to update tag',
        description: error.message,
        variant: 'error',
      });
    },
    onSettled(_data, _error, {recordId}) {
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectRecordLists(typeId),
      });
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectRecord(typeId, recordId, tenantId),
      });
      // The Library renders its record buckets from the batch-slice query, a
      // distinct key from objectRecordLists. Bust it so an edit made from the
      // Library bucket pencil refreshes the bucket list immediately.
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectRecordsBatchAll(),
      });
    },
  });

  const archiveMutation = useMutation({
    async mutationFn(recordId: string) {
      return api.archiveObjectRecord(typeId, recordId);
    },
    async onMutate(recordId: string) {
      await queryClient.cancelQueries({
        queryKey: masterDataKeys.objectRecordLists(typeId),
      });
      const queries = queryClient.getQueriesData<
        PaginatedResponse<ObjectRecord>
      >({
        queryKey: masterDataKeys.objectRecordLists(typeId),
      });
      const previous = queries.map(([key, data]) => [key, data] as const);
      for (const [key, data] of queries) {
        if (data) {
          queryClient.setQueryData(key, {
            ...data,
            items: data.items.map((r) =>
              r.id === recordId
                ? {...r, deletedAt: new Date().toISOString()}
                : r
            ),
          });
        }
      }

      return {previous};
    },
    onSuccess(record) {
      toast({
        title: 'Tag archived',
        description: `${record.code} has been archived`,
        variant: 'success',
      });
      trackProductEvent('resource.updated', {
        resourceType: 'object_record',
        typeId,
        recordId: record.id,
        archived: true,
      });
    },
    onError(error: Error, _id, context) {
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data);
        }
      }

      toast({
        title: 'Failed to archive tag',
        description: error.message,
        variant: 'error',
      });
    },
    onSettled(_data, _error, recordId) {
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectRecordLists(typeId),
      });
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectRecord(typeId, recordId, tenantId),
      });
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectRecordsBatchAll(),
      });
    },
  });

  const restoreMutation = useMutation({
    async mutationFn(recordId: string) {
      return api.restoreObjectRecord(typeId, recordId);
    },
    async onMutate(recordId: string) {
      await queryClient.cancelQueries({
        queryKey: masterDataKeys.objectRecordLists(typeId),
      });
      const queries = queryClient.getQueriesData<
        PaginatedResponse<ObjectRecord>
      >({
        queryKey: masterDataKeys.objectRecordLists(typeId),
      });
      const previous = queries.map(([key, data]) => [key, data] as const);
      for (const [key, data] of queries) {
        if (data) {
          queryClient.setQueryData(key, {
            ...data,
            items: data.items.map((r) =>
              r.id === recordId ? {...r, deletedAt: null} : r
            ),
          });
        }
      }

      return {previous};
    },
    onSuccess(record) {
      toast({
        title: 'Tag restored',
        description: `${record.code} is now active`,
        variant: 'success',
      });
      trackProductEvent('resource.updated', {
        resourceType: 'object_record',
        typeId,
        recordId: record.id,
        archived: false,
      });
    },
    onError(error: Error, _id, context) {
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data);
        }
      }

      toast({
        title: 'Failed to restore tag',
        description: error.message,
        variant: 'error',
      });
    },
    onSettled(_data, _error, recordId) {
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectRecordLists(typeId),
      });
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectRecord(typeId, recordId, tenantId),
      });
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectRecordsBatchAll(),
      });
    },
  });

  const deleteMutation = useMutation({
    async mutationFn(recordId: string) {
      return api.deleteObjectRecord(typeId, recordId);
    },
    onSuccess(_data, recordId) {
      toast({
        title: 'Tag deleted',
        description: 'The tag has been permanently removed',
        variant: 'success',
      });
      trackProductEvent('resource.updated', {
        resourceType: 'object_record',
        typeId,
        recordId,
        deleted: true,
      });
    },
    onError(error: Error) {
      if (isApiError(error) && error.status === 409) {
        toast({
          title: 'Cannot delete',
          description:
            'This record is referenced by other records. Remove those references first.',
          variant: 'error',
        });
        return;
      }

      toast({
        title: 'Failed to delete tag',
        description: error.message,
        variant: 'error',
      });
    },
    onSettled() {
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectRecordLists(typeId),
      });
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectTypeLists(),
      });
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectRecordsBatchAll(),
      });
    },
  });

  return {
    createMutation,
    updateMutation,
    archiveMutation,
    restoreMutation,
    deleteMutation,
  };
}
